import { formatTime } from "@circle-react/components/shared/uikit/TipTap/VoiceMessage/helpers";
import { classNames } from "@circle-react/helpers/twMergeWithCN";
import { useWavesurfer } from "@wavesurfer/react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { t } from "@/i18n-js/instance";
import { AudioTranscriptions } from "@/react/components/shared/uikit/TipTapBlockEditor/FileExtension/AudioTranscription";
import {
  anchorClassNames,
  legacyClassNames,
} from "@/react/helpers/cssAnchorHelpers";

import "./audio-player.scss";

const VOICE_MESSAGE_SPEED_OPTIONS = [0.5, 1, 1.5, 2] as const;

const formatSpeed = (speed: number) => `${speed}x`;

export interface WaveSurferAudioPlayerProps {
  isVoiceMessage?: boolean;
  src: string;
  transcriptionId?: number | null;
  transcriptionPreview?: string | null;
}

export const WaveSurferAudioPlayer = ({
  src,
  isVoiceMessage = false,
  transcriptionId,
  transcriptionPreview,
}: WaveSurferAudioPlayerProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const waveformRef = useRef<HTMLDivElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [playbackSpeed, setPlaybackSpeed] = useState(1);

  const wsOptions = useMemo(
    () => ({
      container: waveformRef,
      url: src,
      waveColor: "#E4E7EB",
      progressColor: "#191B1F",
      cursorColor: "transparent",
      barWidth: 3,
      barGap: 3,
      barRadius: 4,
      height: 30,
      normalize: true,
    }),
    [src],
  );

  const { wavesurfer } = useWavesurfer(wsOptions);

  useEffect(() => {
    if (!wavesurfer) return;

    const unsubs = [
      wavesurfer.on("play", () => setIsPlaying(true)),
      wavesurfer.on("pause", () => setIsPlaying(false)),
      wavesurfer.on("timeupdate", (time: number) => setCurrentTime(time)),
      wavesurfer.on("ready", (dur: number) => setDuration(dur)),
    ];

    return () => unsubs.forEach(fn => fn());
  }, [wavesurfer]);

  useEffect(() => {
    if (wavesurfer && playbackSpeed !== 1) {
      wavesurfer.setPlaybackRate(playbackSpeed);
    }
  }, [wavesurfer, playbackSpeed]);

  const handlePlayPause = useCallback(() => {
    void wavesurfer?.playPause();
  }, [wavesurfer]);

  const handleSpeedChange = useCallback(
    (speed: number) => {
      wavesurfer?.setPlaybackRate(speed);
      setPlaybackSpeed(speed);
    },
    [wavesurfer],
  );

  const handleCycleSpeed = useCallback(() => {
    const currentSpeedIndex = VOICE_MESSAGE_SPEED_OPTIONS.indexOf(
      playbackSpeed as (typeof VOICE_MESSAGE_SPEED_OPTIONS)[number],
    );
    const nextSpeed =
      VOICE_MESSAGE_SPEED_OPTIONS[
        (currentSpeedIndex + 1) % VOICE_MESSAGE_SPEED_OPTIONS.length
      ];

    handleSpeedChange(nextSpeed);
  }, [handleSpeedChange, playbackSpeed]);

  const audioElement = useMemo(
    () => wavesurfer?.getMediaElement() ?? null,
    [wavesurfer],
  );

  return (
    <div ref={containerRef} className="w-full">
      <div
        className={classNames(
          legacyClassNames("audio-player-wavesurfer"),
          isVoiceMessage
            ? classNames(legacyClassNames("voice-message"), "h-12")
            : classNames(anchorClassNames("default"), "mt-2"),
        )}
      >
        <button
          type="button"
          className={legacyClassNames("audio-player-wavesurfer__play-btn")}
          onClick={handlePlayPause}
          aria-label={
            isPlaying
              ? t("tiptap.audio_player.pause")
              : t("tiptap.audio_player.play")
          }
        >
          {isPlaying ? (
            <svg viewBox="0 0 16 16" fill="currentColor" width="14" height="14">
              <path d="M5.5 3.5A1.5 1.5 0 0 1 7 5v6a1.5 1.5 0 0 1-3 0V5a1.5 1.5 0 0 1 1.5-1.5zm5 0A1.5 1.5 0 0 1 12 5v6a1.5 1.5 0 0 1-3 0V5a1.5 1.5 0 0 1 1.5-1.5z" />
            </svg>
          ) : (
            <svg viewBox="0 0 16 16" fill="currentColor" width="14" height="14">
              <path d="M4.25 3.02a.75.75 0 0 1 .77.04l8 5.25a.75.75 0 0 1 0 1.26l-8 5.25A.75.75 0 0 1 3.75 14V3.5a.75.75 0 0 1 .5-.48z" />
            </svg>
          )}
        </button>

        <div
          ref={waveformRef}
          className={legacyClassNames("audio-player-wavesurfer__waveform")}
        />

        <span
          className={legacyClassNames("audio-player-wavesurfer__time")}
          aria-hidden
        >
          {isPlaying ? formatTime(currentTime) : formatTime(duration)}
        </span>

        <button
          type="button"
          className="border-primary text-primary hover:bg-hover relative shrink-0 flex h-7 w-11 items-center justify-center rounded-md border bg-transparent px-0 text-xs font-medium transition-colors tabular-nums"
          aria-label={t("tiptap.audio_player.speed")}
          onClick={handleCycleSpeed}
        >
          {formatSpeed(playbackSpeed)}
        </button>
      </div>
      {transcriptionId && transcriptionPreview && audioElement && (
        <AudioTranscriptions
          compact
          transcriptionId={transcriptionId}
          transcriptionPreview={transcriptionPreview}
          audioPlayer={audioElement}
        />
      )}
    </div>
  );
};
